/**
 * Reserve with Google (RwG) conversion tracking — client-side cookie variant of the redirect
 * integration. Google appends `rwg_token` to the landing URL; we persist it in a first-party
 * cookie and POST a conversion event back to Google on a successful booking.
 *
 * Environment-specific values (collect endpoint, cookie domain) are injected via `RwgConfig`
 * rather than read here, so each app can source them from its own env config — the marketing
 * Next.js bundle has no access to the CRA `REACT_APP_*` vars and vice versa.
 *
 * Spec: https://developers.google.com/actions-center/verticals/appointments/redirect/integration-steps/conversion-tracking
 */

import { deleteCookie, getCookie, setCookie } from './cookies';

export const RWG_TOKEN_COOKIE = '_rwgToken';
export const RWG_REFERRED_ZONE_COOKIE = '_rwgZone';

/** Query param Google appends to the landing URL. */
export const RWG_TOKEN_PARAM = 'rwg_token';
/** Query param the marketing proxy uses to carry the referred zone to the customer app. */
export const RWG_REFERRAL_ZONE_PARAM = 'zone';

/** Google mandates a minimum 30-day attribution window. */
export const RWG_TOKEN_MAX_AGE_SECONDS = 2_592_000;

export const RWG_PROD_COLLECT_ENDPOINT = 'https://www.google.com/maps/conversion/collect';
export const RWG_SANDBOX_COLLECT_ENDPOINT = 'https://www.google.com/maps/conversion/debug/collect';

/** Booking completed with the originally referred merchant. */
export const RWG_MERCHANT_UNCHANGED = 2;
/** Booking completed with a different merchant than referred. */
export const RWG_MERCHANT_CHANGED = 1;

type MerchantChanged = typeof RWG_MERCHANT_UNCHANGED | typeof RWG_MERCHANT_CHANGED;

export interface RwgConfig {
  /** Google's collect endpoint — the prod collector or the debug sandbox. */
  collectEndpoint: string;
  /** Conversion partner ID issued by the RwG aggregator (SOCi). */
  conversionPartnerId: string;
  /**
   * Registrable domain the cookies are scoped to (e.g. `.metropolis.io`) so a token captured on
   * the marketing host is readable on the customer app host. Omit for a host-only cookie.
   */
  cookieDomain?: string;
}

/**
 * Google's live collector counts real conversions; every other environment must use the debug
 * sandbox. Callers pass their app's own production signal rather than this module sniffing the
 * hostname, so a preview deploy on a prod domain can't silently report live conversions.
 */
export function getCollectEndpoint(isProduction: boolean): string {
  return isProduction ? RWG_PROD_COLLECT_ENDPOINT : RWG_SANDBOX_COLLECT_ENDPOINT;
}

/**
 * Registrable domain (dot-prefixed) for an app URL, so a cookie written on one subdomain is
 * readable on the others: `https://go.staging.metrop.io` → `.metrop.io`. Returns `undefined`
 * for localhost/IPs/malformed input, yielding a host-only cookie.
 *
 * Takes the URL rather than reading `window.location` so the value follows deploy config
 * (`REACT_APP_CUSTOMER_APP_URL` / `NEXT_PUBLIC_CUSTOMER_APP_URL`) instead of whatever host the
 * page happens to be served from.
 */
export function getCookieDomainForUrl(url: string): string | undefined {
  let hostname: string;
  try {
    ({ hostname } = new URL(url));
  } catch {
    return undefined;
  }

  const labels = hostname.split('.');
  // Need at least two labels (e.g. `metrop.io`); localhost / single-label / IPs stay host-only.
  if (labels.length < 2 || labels.some((label) => label === '' || /^\d+$/.test(label))) {
    return undefined;
  }

  return `.${labels.slice(-2).join('.')}`;
}

export function getRwgToken(): string | undefined {
  return getCookie(RWG_TOKEN_COOKIE);
}

export function getReferredZone(): string | undefined {
  return getCookie(RWG_REFERRED_ZONE_COOKIE);
}

interface RwgReferral {
  /** Zone/site the RwG link referred the user to, when the landing page knows it. */
  referredZone?: string;
  /** The `rwg_token` from the landing URL. */
  token: string;
}

/**
 * Persists a Google referral for the attribution window.
 *
 * The token and referred zone are written together so they can never describe different
 * referrals. A *new* token starts a fresh referral and clears any previously stored zone; the
 * *same* token only ever adds zone information. That keeps the result independent of mount
 * order, since a zone landing page captures twice — once from the zone-scoped mount that knows
 * the zone, and once from the root layout mount that does not.
 */
export function storeRwgReferral({ token, referredZone }: RwgReferral, config: RwgConfig): void {
  if (!token) {
    return;
  }

  const { cookieDomain } = config;
  const isNewReferral = getRwgToken() !== token;

  setCookie(RWG_TOKEN_COOKIE, token, {
    maxAgeSeconds: RWG_TOKEN_MAX_AGE_SECONDS,
    domain: cookieDomain,
  });

  if (referredZone) {
    setCookie(RWG_REFERRED_ZONE_COOKIE, referredZone, {
      maxAgeSeconds: RWG_TOKEN_MAX_AGE_SECONDS,
      domain: cookieDomain,
    });
  } else if (isNewReferral) {
    // Otherwise a zone from an earlier referral would be compared against this booking.
    deleteCookie(RWG_REFERRED_ZONE_COOKIE, { domain: cookieDomain });
  }
}

interface ReportRwgConversionOptions {
  /** Defaults to 2 (merchant unchanged); set 1 only if the user switched merchant. */
  merchantChanged?: MerchantChanged;
}

/** Reports a conversion to Google; no-ops without a stored token. Errors are swallowed — best-effort. */
export async function reportRwgConversion(
  config: RwgConfig,
  { merchantChanged = RWG_MERCHANT_UNCHANGED }: ReportRwgConversionOptions = {},
): Promise<void> {
  const rwgToken = getRwgToken();
  if (!rwgToken) {
    return;
  }

  try {
    await fetch(config.collectEndpoint, {
      method: 'POST',
      // Google only accepts text/plain; any other Content-Type triggers a CORS failure.
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({
        conversion_partner_id: config.conversionPartnerId,
        rwg_token: rwgToken,
        merchant_changed: merchantChanged,
      }),
      // keepalive lets the request complete across the route change that follows booking success.
      keepalive: true,
    });
  } catch {
    // Intentionally ignored — conversion tracking is best-effort.
  }
}
